import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { EmailQuota } from '@account-users/account/models/EmailQuota';

class EmailQuotaValidators {
  static monthlyQuota(control: AbstractControl): Record<string, unknown> {
    return !control.value || control.value <= 0
      ? { badQuota: control.value }
      : null;
  }
}

@Component({
  selector: 'app-email-quota',
  templateUrl: './email-quota.component.html',
  styleUrls: ['./email-quota.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailQuotaComponent implements OnChanges {
  @HostBinding('class.disabled') @Input() disabled: boolean;
  @Input() quota: EmailQuota | undefined;
  @Input() loadingMonthlyQuota = false;
  @Input() loadingOneTimeQuota = false;
  @Output() addQuota = new EventEmitter<{
    quota: Partial<EmailQuota>;
    orderNumber: number;
    type: string;
  }>();

  public monthlyQuota: FormGroup;
  public oneTimeQuota: FormGroup;
  public canSendEmails = 0;
  public lastDate: string | null;

  get emailQuota() {
    return this.monthlyQuota.get('emailQuota');
  }

  get monthyQuotaOrderId() {
    return this.monthlyQuota.get('orderNumber');
  }

  get oneTimeQuotaOrderId() {
    return this.oneTimeQuota.get('orderNumber');
  }

  constructor(private readonly fb: FormBuilder) {
    this.monthlyQuota = this.fb.group({
      emailQuota: [0, [EmailQuotaValidators.monthlyQuota]],
      orderNumber: [
        '',
        [Validators.required, Validators.pattern(/^[A-Za-z0-9]{16}$/)],
      ],
    });
    this.oneTimeQuota = this.fb.group({
      emailOneTimeQuota: [0],
      orderNumber: [
        '',
        [Validators.required, Validators.pattern(/^[A-Za-z0-9]{16}$/)],
      ],
    });
  }

  public ngOnChanges(changes: SimpleChanges) {
    const quota = changes['quota'];
    if (quota?.currentValue) {
      const { emailQuota, emailOneTimeQuota } = quota.currentValue;
      this.monthlyQuota.patchValue({ emailQuota });
      this.oneTimeQuota.patchValue({ emailOneTimeQuota });
      this.canSendEmails = emailQuota + emailOneTimeQuota;
      this.lastDate = !!emailOneTimeQuota ? this.lastDateOfMonth() : '';
    }
  }

  public addMonthlyQuota(type: string) {
    const { emailQuota, orderNumber } = this.monthlyQuota.getRawValue();
    this.addQuota.emit({ quota: { emailQuota }, orderNumber, type });
    this.monthyQuotaOrderId.reset();
  }

  public addOneTimeQuota(type: string) {
    const { emailOneTimeQuota, orderNumber } = this.oneTimeQuota.getRawValue();
    this.addQuota.emit({ quota: { emailOneTimeQuota }, orderNumber, type });
    this.oneTimeQuotaOrderId.reset();
  }

  private lastDateOfMonth(): string {
    const date = new Date();
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = new Date(year, month, 0).getDate();
    return `${year}-${month}-${day}`;
  }
}
